import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { FOSSABOT_DEP_UPGRADE_PRS } from '../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import ResolvedReason from '../ResolvedReason';
import CPEBanner from '../Vulnerability/CPEBanner';
import CustomRiskScoreDetails from '../Vulnerability/CustomRiskScoreDetails';
import Dependency from '../Vulnerability/Dependency';
import Description from '../Vulnerability/Description';
import Details from '../Vulnerability/Details';
import Footer from '../Vulnerability/Footer';
import { FossabotRemediationPanel } from '../Vulnerability/FossabotRemediationPanel';
import Header from '../Vulnerability/Header';
import MalwareDescription from '../Vulnerability/MalwareDescription';
import NVDBaseMetrics from '../Vulnerability/NVDBaseMetrics';
import References from '../Vulnerability/References';
import Remediation from '../Vulnerability/Remediation';
import type { IssueDetailsTab } from '../Tabs';

import IssueGenericContent from './IssueGenericContent';
import VulnerabilityIssueLoading from './Loading/VulnerabilityIssueLoading';

type VulnerabilityIssueDrawerContentProps = {
  activeTab: IssueDetailsTab;
  setActiveTab: (tab: IssueDetailsTab) => void;
};

const VulnerabilityIssueContent: VoidFunctionComponent<VulnerabilityIssueDrawerContentProps> = ({
  activeTab,
  setActiveTab,
}) => {
  const isLoadingIssueDetails = useSelector(IssuesV2Store.selectors.getIsLoadingIssueDetails);
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const isFossabotEnabled = useIsFeatureEnabled(FOSSABOT_DEP_UPGRADE_PRS);
  const hasPermission = useHasPermission();
  const shouldShowFossabotRemediation = isFossabotEnabled && hasPermission({
    action: Action.View,
    resourceType: ResourceType.OrganizationFossabotUpgrade,
  });

  if (isLoadingIssueDetails) {
    return <VulnerabilityIssueLoading />;
  }

  const isMalware = issueDetails?.type === 'malware';

  if (isMalware) {
    return (
      <IssueGenericContent
        headerSlot={<Header />}
        contentSlot={
          <>
            <ResolvedReason />
            <MalwareDescription malwareRevisionId={issueDetails.malwareRevisionId} />
            <Dependency />
          </>
        }
        setActiveTab={setActiveTab}
        activeTab={activeTab}
      />
    );
  }

  return (
    <IssueGenericContent
      headerSlot={<Header />}
      contentSlot={
        <>
          <CPEBanner />
          <ResolvedReason />
          <CustomRiskScoreDetails />
          {shouldShowFossabotRemediation ? <FossabotRemediationPanel /> : <Remediation />}
          <Details />
          <Dependency />
          <Description />
          <NVDBaseMetrics />
          <References />
          <Footer />
        </>
      }
      setActiveTab={setActiveTab}
      activeTab={activeTab}
    />
  );
};

export default VulnerabilityIssueContent;
